import { initSwiper } from '../../components/swiper';

const INLINE_ATC = '[data-inline-related-atc]';
const INLINE_HOST = '[data-related-products-inline-host]';
const SEL_RELATED_PRODUCTS = '[data-related-products]';

const ATTR_RELATED_PRODUCTS_SOURCE = 'data-related-products-source';
const RELATED_SOURCE_INLINE = 'pdp-inline';
const RELATED_SOURCE_MAIN = 'pdp-main';
const SEL_SRC_INLINE = `[${ATTR_RELATED_PRODUCTS_SOURCE}="${RELATED_SOURCE_INLINE}"]`;
const SEL_SRC_MAIN = `[${ATTR_RELATED_PRODUCTS_SOURCE}="${RELATED_SOURCE_MAIN}"]`;
const SEL_RELATED_BLOCK_INLINE = `[data-related-products]${SEL_SRC_INLINE}`;
const SEL_RELATED_BLOCK_MAIN = `[data-related-products]${SEL_SRC_MAIN}`;
const SEL_INLINE_RELATED_ROOT =
  `[data-related-products-inline-root]${SEL_SRC_INLINE}`;

/** Buying column only: never exceed ~1.5 cards (footer fallback HTML uses wider breakpoints). */
const INLINE_BUYING_SWIPER_CONFIG = {
  mousewheel: { enabled: true, forceToAxis: true },
  loop: true,
  slidesPerView: 1.5,
  spaceBetween: 10,
  breakpoints: {
    768: { slidesPerView: 1.5, spaceBetween: 16 },
    1024: { slidesPerView: 1.5, spaceBetween: 20 },
  },
};

const isBuyingColumnRelatedCarousel = (containerEl) =>
  containerEl?.getAttribute(ATTR_RELATED_PRODUCTS_SOURCE) ===
    RELATED_SOURCE_INLINE || Boolean(containerEl?.closest(INLINE_ATC));

/** Footer section HTML includes next/prev; `lg:!flex` beats our CSS — remove nodes under the ATC mount. */
const stripBuyingColumnSwiperNav = (containerEl) => {
  if (!isBuyingColumnRelatedCarousel(containerEl)) return;
  containerEl
    .querySelectorAll(
      '.swiper-button, .swiper-button-next, .swiper-button-prev, .swiper-pagination',
    )
    .forEach((node) => node.remove());
};

/**
 * Compact carousel under ATC: mount exists only when Product → “Show related under add to cart” is on.
 * Hidden host section supplies [data-related-products]; we move it into the mount then drop the host.
 */
const setupInlineRelatedUnderAtc = () => {
  const atc = document.querySelector(INLINE_ATC);
  const host = document.querySelector(INLINE_HOST);

  if (host) {
    const inner = host.querySelector(SEL_RELATED_PRODUCTS);
    if (atc && inner) {
      atc.appendChild(inner);
      host.remove();
    }
    return;
  }

  // Saved theme missing the inline section: still show under ATC using the footer section URL.
  if (!atc || atc.querySelector(SEL_RELATED_PRODUCTS)) return;
  const main = document.querySelector(SEL_RELATED_BLOCK_MAIN);
  const url = main?.dataset?.url || main?.getAttribute('data-url');
  if (!url) return;

  const shell = document.createElement('div');
  shell.setAttribute('data-related-products', '');
  shell.setAttribute(ATTR_RELATED_PRODUCTS_SOURCE, RELATED_SOURCE_INLINE);
  shell.setAttribute('data-url', url);
  atc.appendChild(shell);
};

const initRelatedProducts = () => {
  setupInlineRelatedUnderAtc();

  const relatedProductsEls = document.querySelectorAll(SEL_RELATED_PRODUCTS);
  if (!relatedProductsEls.length) return;

  const applyParsedCarousel = (el, carouselEl) => {
    if (!carouselEl?.innerHTML?.trim()) return false;
    el.innerHTML = carouselEl.innerHTML;
    stripBuyingColumnSwiperNav(el);
    const sliderEl = el.querySelector('[data-swiper-related-products]');
    if (sliderEl) {
      if (isBuyingColumnRelatedCarousel(el)) {
        sliderEl.setAttribute(
          'data-swiper-config',
          JSON.stringify(INLINE_BUYING_SWIPER_CONFIG),
        );
      }
      initSwiper(sliderEl);
    }
    return true;
  };

  const fetchResults = async (el) => {
    const readUrl = () => el.dataset.url || el.getAttribute('data-url');
    let url = readUrl();
    if (!url) return;

    const pickCarouselFragment = (doc) => {
      const source = el.getAttribute(ATTR_RELATED_PRODUCTS_SOURCE);
      if (source === RELATED_SOURCE_INLINE) {
        return (
          doc.querySelector(SEL_INLINE_RELATED_ROOT) ||
          doc.querySelector(SEL_RELATED_BLOCK_INLINE) ||
          doc.querySelector(SEL_RELATED_BLOCK_MAIN)
        );
      }
      if (source === RELATED_SOURCE_MAIN) {
        return doc.querySelector(SEL_RELATED_BLOCK_MAIN);
      }
      return doc.querySelector(SEL_RELATED_PRODUCTS);
    };

    const tryFetch = async (fetchUrl) => {
      const response = await fetch(fetchUrl);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      if (doc.querySelector('parsererror')) return false;
      const carouselEl = pickCarouselFragment(doc);
      return applyParsedCarousel(el, carouselEl);
    };

    try {
      let ok = await tryFetch(url);
      if (
        !ok &&
        el.getAttribute(ATTR_RELATED_PRODUCTS_SOURCE) === RELATED_SOURCE_INLINE
      ) {
        const main = document.querySelector(SEL_RELATED_BLOCK_MAIN);
        const fallback = main?.dataset?.url || main?.getAttribute('data-url');
        if (fallback && fallback !== url) {
          el.setAttribute('data-url', fallback);
          url = fallback;
          ok = await tryFetch(fallback);
        }
      }
      if (!ok) el.innerHTML = '';
    } catch (err) {
      console.error('Error fetching related products:', err);
      el.innerHTML = '';
    }
  };

  relatedProductsEls.forEach((el) => {
    void fetchResults(el);
  });
};

export default initRelatedProducts;
